'use client';
import {
  PhoneOutlined,
  CloseOutlined,
  PushpinOutlined,
} from '@ant-design/icons';
import {
  FacebookOutlined,
  InstagramOutlined,
  TikTokOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { useTranslation } from '@root/src/i18n/client';
import React, { useEffect, useState } from 'react';

const ContactWidget: React.FC = () => {
  const [currentIcon, setCurrentIcon] = useState(0); // Manage the current icon index
  const [isOpen, setIsOpen] = useState(false); // Manage widget open/close state
  const { t } = useTranslation('common');
  // Social media icons array
  const icons = [
    { id: 1, component: <PhoneOutlined className="text-white" /> },
    { id: 2, component: <WhatsAppOutlined className="text-white" /> },
    { id: 3, component: <FacebookOutlined className="text-white" /> },
    { id: 4, component: <InstagramOutlined className="text-white" /> },
    { id: 5, component: <TikTokOutlined className="text-white" /> },
  ];

  const toggleWidget = () => setIsOpen(prev => !prev);
  const closeWidget = () => setIsOpen(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIcon(prev => (prev + 1) % icons.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [icons.length]);

  return (
    <>
      {/* Backdrop - click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={closeWidget}
        />
      )}

      <div>
        {/* Main floating button */}
        <div
          className="fixed bottom-[138px] right-0 z-50 cursor-pointer text-2xl bg-[#ec9c12] hover:bg-[#d4811a]
        py-3 px-4 md:py-5 md:px-6 rounded-l-full shadow-lg transition-all duration-300 ease-in-out transform hover:shadow-xl"
          onClick={(e) => {
            e.stopPropagation();
            toggleWidget();
          }}
          title={t('lytra.contactWidgetTitle')}
        >
          {isOpen ? (
            <CloseOutlined className="text-white" />
          ) : (
            icons[currentIcon].component
          )}
        </div>

        {/* Contact panel */}
        <div
          className={`fixed bottom-[144px] right-0 z-50 flex flex-col bg-gradient-to-b from-white to-gray-50 rounded-l-2xl shadow-2xl transition-all duration-500 ease-out transform origin-right ${isOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full pointer-events-none'
            }`}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 'min(100%, 340px)',
          }}
        >
          {/* Header */}
          <div className="bg-[#ec9c12] text-white px-4 md:px-5 py-3 md:py-4 rounded-tl-2xl border-b-2 border-[#d4811a]">
            <h3 className="text-sm md:text-base font-bold">{t('lytra.contactWidgetTitle')}</h3>
            <p className="text-xs text-orange-50 mt-0.5">{t('lytra.contactWidgetSubtitle')}</p>
          </div>

          {/* Contact items */}
          <div className="p-3 md:p-4 space-y-2">
            {/* Phone */}
            <a
              href="tel:0931338566"
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-orange-100 transition duration-300 group"
            >
              <div className="bg-[#ec9c12] text-white rounded-full p-2 group-hover:bg-[#d4811a] transition flex-shrink-0">
                <PhoneOutlined className="text-base" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 leading-none">{t('lytra.callLabel')}</p>
                <p className="text-xs text-gray-800 font-semibold truncate">0931 338 566</p>
              </div>
            </a>

            {/* Zalo */}
            <a
              href="https://zalo.me/0931338566"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-blue-100 transition duration-300 group"
            >
              <div className="bg-[#0077ff] text-white rounded-full p-2 group-hover:bg-[#0066cc] transition flex-shrink-0">
                <WhatsAppOutlined className="text-base" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 leading-none">Zalo</p>
                <p className="text-xs text-gray-800 font-semibold truncate">0931 338 566</p>
              </div>
            </a>

            {/* Facebook */}
            <a
              href="https://www.facebook.com/share/1DToga9jmY/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-blue-100 transition duration-300 group"
            >
              <div className="bg-[#1877f2] text-white rounded-full p-2 group-hover:bg-[#0f63d9] transition flex-shrink-0">
                <FacebookOutlined className="text-base" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 leading-none">Facebook</p>
                <p className="text-xs text-gray-800 font-semibold truncate">ly.lep.9</p>
              </div>
            </a>

            {/* Instagram */}
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-pink-100 transition duration-300 group"
            >
              <div className="bg-gradient-to-r from-[#fd1d1d] to-[#e1306c] text-white rounded-full p-2 group-hover:shadow-lg transition flex-shrink-0">
                <InstagramOutlined className="text-base" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 leading-none">Instagram</p>
                <p className="text-xs text-gray-800 font-semibold">Follow</p>
              </div>
            </a>

            {/* TikTok */}
            <a
              href="https://tiktok.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-200 transition duration-300 group"
            >
              <div className="bg-black text-white rounded-full p-2 group-hover:bg-gray-800 transition flex-shrink-0">
                <TikTokOutlined className="text-base" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 leading-none">TikTok</p>
                <p className="text-xs text-gray-800 font-semibold">Follow</p>
              </div>
            </a>

            {/* Location */}
            <a
              href="https://www.google.com/maps/place/Tr%E1%BA%A7m+H%C6%B0%C6%A1ng+M%E1%BB%99C+AN/@10.8155296,106.6947192,13z/data=!4m9!1m2!2m1!1s+66%2F37D,+Xo+Viet+Nghe+Tinh+Street,+Ward+19,+Binh+Thanh+District,+Ho+Chi+Minh+City.'!3m5!1s0x317529af30aa7277:0xb7018a53801433c8!8m2!3d10.7952!4d106.710986!16s%2Fg%2F11sc3k9v9w?hl=vi-VN&entry=ttu&g_ep=EgoyMDI1MDIxMi4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-red-100 transition duration-300 group"
            >
              <div className="bg-red-500 text-white rounded-full p-2 group-hover:bg-red-600 transition flex-shrink-0">
                <PushpinOutlined className="text-base" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 leading-none">{t('Address')}</p>
                <p className="text-xs text-gray-800 font-semibold truncate">
                  {t('AddressContactss')}
                </p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactWidget;
